import { useEffect, useRef } from 'react'
import type { ProgressState } from '@/types'
import { GlassCard } from '@/components/common/GlassCard'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { getProgressLogIcon, isProgressLogComplete, isProgressLogError } from '@/lib/progress-log-icons'
import { cn } from '@/lib/utils'

interface ProgressPanelProps {
  progress: ProgressState
}

function scrollLogToBottom(container: HTMLDivElement | null) {
  if (!container) return
  container.scrollTop = container.scrollHeight
}

export function ProgressPanel({ progress }: ProgressPanelProps) {
  const logContainerRef = useRef<HTMLDivElement>(null)
  const logCount = progress.logs.length

  useEffect(() => {
    if (logCount === 0) return
    scrollLogToBottom(logContainerRef.current)
    const frame = requestAnimationFrame(() => {
      scrollLogToBottom(logContainerRef.current)
    })
    return () => cancelAnimationFrame(frame)
  }, [logCount])

  return (
    <div className="p-8 pb-4">
      <GlassCard className="max-w-2xl mx-auto">
        <div className="mb-4 flex items-start justify-between gap-3 text-left">
          <div className="text-left">
            <div className="text-[var(--lm-foreground-strong)] dark:text-gray-300">{progress.message}</div>
            <div
              className={cn(
                'text-xs',
                progress.isConnected
                  ? 'text-[#027a48] dark:text-green-400'
                  : 'text-[var(--lm-muted)] dark:text-gray-500'
              )}
            >
              ● {progress.isConnected ? 'Processing...' : 'Waiting for response...'}
            </div>
          </div>
          <LoadingSpinner className="mt-0.5 shrink-0" />
        </div>
        <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-[var(--surface-sunken)] dark:bg-white/10">
          <div
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 transition-all duration-300"
            style={{ width: `${progress.progress}%` }}
          />
        </div>
        <div ref={logContainerRef} className="progress-log max-h-48 overflow-y-auto rounded-lg p-3">
          {progress.logs.map((log, index) => {
            const isActive =
              index === progress.logs.length - 1 &&
              progress.isConnected &&
              !isProgressLogComplete(log.message)
            const isError = isProgressLogError(log.step, log.message)
            const isComplete = isProgressLogComplete(log.message)
            const Icon = getProgressLogIcon(log.step, log.message)

            return (
              <div
                key={`${log.step ?? 'log'}-${index}`}
                className="flex items-start gap-2.5 border-b border-[var(--lm-border-subtle)] py-2 last:border-0 dark:border-white/5"
              >
                <Icon
                  className={cn(
                    'mt-0.5 h-4 w-4 shrink-0',
                    isError
                      ? 'text-[var(--tint-red-fg)] dark:text-red-400'
                      : isComplete
                        ? 'text-[#027a48] dark:text-green-400/90'
                      : isActive
                        ? 'text-primary dark:text-violet-400'
                        : 'text-[var(--lm-muted)] dark:text-slate-500'
                  )}
                />
                <span
                  className={cn(
                    'text-sm leading-relaxed',
                    isError
                      ? 'text-[var(--tint-red-fg)] dark:text-red-400'
                      : isComplete
                        ? 'text-[#027a48] dark:text-green-400/90'
                      : isActive
                        ? 'text-[var(--lm-foreground-strong)] dark:text-violet-300'
                        : 'text-[var(--lm-muted)] dark:text-slate-400'
                  )}
                >
                  {log.message}
                </span>
              </div>
            )
          })}
        </div>
      </GlassCard>
    </div>
  )
}
